<template>
  <section class="ws-act-users-section">
    <div class="ws-act-users-head">
      <h2 class="ws-act-users-head__title">{{ t('workspaceAtividade.userSummary.title') }}</h2>
    </div>
    <div class="ws-act-user-grid">
      <div
        v-for="u in usuarios"
        :key="u.usuarioId"
        role="button"
        tabindex="0"
        class="ws-act-user-card"
        :class="{
          'ws-act-user-card--selected': selectedId === u.usuarioId,
          'ws-act-user-card--live': u.atividadeAtiva,
          'ws-act-user-card--has-tasks': activeTasks(u).length > 0
        }"
        @click="emit('select', u.usuarioId)"
        @keydown.enter.prevent="emit('select', u.usuarioId)"
        @keydown.space.prevent="emit('select', u.usuarioId)"
      >
        <div class="ws-act-user-card__header">
          <div class="ws-act-user-card__avatar-wrap">
            <span v-if="u.atividadeAtiva" class="ws-act-user-card__live-pulse" aria-hidden="true" />
            <img
              v-if="avatarUrl(u)"
              :src="avatarUrl(u)"
              :alt="u.nome"
              class="ws-act-user-card__avatar"
            />
            <span v-else class="ws-act-user-card__fallback">{{ initials(u.nome) }}</span>
            <span
              v-if="u.atividadeAtiva"
              class="ws-act-user-card__live-badge"
              :title="t('workspaceAtividade.userSummary.working')"
            >
              <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </span>
          </div>
          <div class="ws-act-user-card__intro">
            <div class="ws-act-user-card__title-row">
              <span class="ws-act-user-card__name">{{ u.nome }}</span>
              <span class="ws-act-user-card__meta">
                {{ u.qtdSessoes }} {{ t('workspaceAtividade.userSummary.sessions') }}
                ·
                {{ u.totalFormatado || formatMin(u.totalMinutos) }}
              </span>
            </div>
            <div v-if="u.atividadeAtiva" class="ws-act-user-card__live-strip">
              <span class="ws-act-user-card__live-bolt" aria-hidden="true">
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 2 3 14h8l-1 10 10-14h-8l1-8z"/>
                </svg>
              </span>
              <span>
                {{ t('workspaceAtividade.userSummary.working') }}
                <strong>{{ elapsedLabel(u) }}</strong>
              </span>
            </div>
          </div>
        </div>

        <div v-if="activeTasks(u).length" class="ws-act-user-card__live-tasks">
          <button
            v-for="(ativa, idx) in activeTasks(u)"
            :key="ativa.taskId || idx"
            type="button"
            class="ws-act-live-task-card"
            :aria-label="t('workspaceAtividade.userSummary.openActiveTask', { title: ativa.taskTitulo })"
            @click.stop="onOpenTask(ativa.taskId)"
          >
            <span
              class="ws-act-live-task-card__accent"
              :style="{ backgroundColor: taskColor(ativa) }"
              aria-hidden="true"
            />
            <span class="ws-act-live-task-card__inner">
              <span class="ws-act-live-task-card__title-row">
                <span class="ws-act-live-task-card__title" :title="ativa.taskTitulo">
                  {{ ativa.taskTitulo }}
                </span>
                <span class="ws-act-live-task-card__timer">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {{ elapsedFor(ativa) }}
                </span>
              </span>

              <p
                v-if="taskDescricao(ativa)"
                class="ws-act-live-task-card__desc"
                :title="taskDescricao(ativa)"
              >
                {{ taskDescricao(ativa) }}
              </p>

              <div class="ws-act-live-task-card__progress">
                <div class="ws-act-live-task-card__progress-head">
                  <span class="ws-act-live-task-card__progress-label">
                    {{ t('kanban.taskDetail.sections.progress.completed') }}
                  </span>
                  <span class="ws-act-live-task-card__progress-pct">{{ progressPct(ativa) }}%</span>
                </div>
                <div class="ws-act-live-task-card__progress-track" aria-hidden="true">
                  <span
                    class="ws-act-live-task-card__progress-fill"
                    :style="progressBarStyle(ativa)"
                  />
                </div>
              </div>

              <span class="ws-act-live-task-card__meta">
                <span
                  class="ws-act-chip"
                  :class="prazoClass(ativa)"
                  :title="t('workspaceAtividade.userSummary.deadline')"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  {{ formatPrazo(ativa.prazoTarefa) }}
                </span>
                <span
                  v-if="checklistTotal(ativa) > 0"
                  class="ws-act-chip ws-act-chip--check"
                  :title="t('workspaceAtividade.userSummary.checklist')"
                >
                  <span class="ws-act-chip__check-track" aria-hidden="true">
                    <span
                      class="ws-act-chip__check-fill"
                      :style="{ width: checklistPct(ativa) + '%', backgroundColor: taskColor(ativa) }"
                    />
                  </span>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {{ checklistDone(ativa) }}/{{ checklistTotal(ativa) }}
                </span>
              </span>

              <span v-if="taskTags(ativa).length" class="ws-act-live-task-card__tags">
                <span
                  v-for="tag in taskTags(ativa)"
                  :key="tag.id"
                  class="ws-act-tag-pill"
                  :style="tagPillStyle(tag)"
                >
                  <span class="ws-act-tag-pill__icon" aria-hidden="true">{{ getTagIcon(tag.icone) }}</span>
                  <span class="ws-act-tag-pill__label">{{ tag.nome }}</span>
                </span>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { resolveMediaUrl } from '@/config/app'
import { formatLocalDate, parseLocalDate, prazoDeadlineStatus } from '@/utils/format'
import { getTagIcon } from '@/utils/iconMapper'

const { t, locale } = useI18n()

const props = defineProps({
  usuarios: { type: Array, default: () => [] },
  selectedId: { type: [Number, String], default: null }
})

const emit = defineEmits(['select', 'open-task'])

const onOpenTask = (taskId) => {
  if (taskId) emit('open-task', taskId)
}

const now = ref(Date.now())
let tickTimer = null

const hasLive = computed(() => props.usuarios.some((u) => u.atividadeAtiva))

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

onMounted(() => {
  tickTimer = setInterval(() => {
    if (hasLive.value) now.value = Date.now()
  }, 60000)
})

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer)
})

const avatarUrl = (u) => {
  const p = u?.fotoPerfil
  if (!p) return ''
  return resolveMediaUrl(p) || p
}

const initials = (name) => {
  if (!name) return '?'
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

const formatMin = (min) => {
  const m = Number(min) || 0
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h > 0) return `${h}h ${r}min`
  return `${r}min`
}

const activeTasks = (u) => {
  const list = u?.atividadesAtivas
  if (Array.isArray(list) && list.length) {
    return list.filter((a) => a?.taskId && a?.taskTitulo)
  }
  if (u?.atividadeAtivaTaskId && u?.atividadeAtivaTaskTitulo) {
    return [{
      taskId: u.atividadeAtivaTaskId,
      taskTitulo: u.atividadeAtivaTaskTitulo,
      taskCor: u.atividadeAtivaTaskCor,
      dataInicio: u.atividadeAtivaDesde,
      tempoAtivoFormatado: u.tempoAtivoFormatado
    }]
  }
  return []
}

const taskColor = (ativa) => ativa?.taskCor || '#06b6d4'

const taskDescricao = (ativa) => {
  const raw = ativa?.taskDescricao
  if (!raw) return ''
  return String(raw)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const progressPct = (ativa) => {
  const p = Number(ativa?.progressoTarefaPercentual)
  if (!Number.isFinite(p)) return 0
  return Math.min(100, Math.max(0, Math.round(p)))
}

const progressBarStyle = (ativa) => {
  const pct = progressPct(ativa)
  let background = 'linear-gradient(90deg, #ef4444, #f97316)'
  if (pct >= 80) background = 'linear-gradient(90deg, #22c55e, #10b981)'
  else if (pct >= 50) background = 'linear-gradient(90deg, #f59e0b, #eab308)'
  return { width: `${pct}%`, background }
}

const taskTags = (ativa) => (Array.isArray(ativa?.tags) ? ativa.tags : [])

const checklistTotal = (ativa) => Number(ativa?.checklistTotal) || 0

const checklistDone = (ativa) => Number(ativa?.checklistConcluidos) || 0

const checklistPct = (ativa) => {
  const total = checklistTotal(ativa)
  if (!total) return 0
  return Math.min(100, Math.round((checklistDone(ativa) / total) * 100))
}

const tagContrast = (hex) => {
  const h = String(hex || '#6366f1').replace('#', '')
  if (h.length !== 6) return '#fff'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? '#0f172a' : '#fff'
}

const tagPillStyle = (tag) => {
  const bg = tag?.cor || '#6366f1'
  return { backgroundColor: bg, color: tagContrast(bg) }
}

const formatPrazo = (prazo) => {
  if (!parseLocalDate(prazo)) return t('workspaceAtividade.userSummary.noDeadline')
  return formatLocalDate(prazo, dateLocale.value)
}

const prazoClass = (ativa) => `ws-act-chip--prazo-${prazoDeadlineStatus(ativa?.prazoTarefa, 3)}`

const elapsedFor = (ativa) => {
  const desde = ativa?.dataInicio
  if (!desde) return ativa?.tempoAtivoFormatado || ''
  const start = new Date(desde).getTime()
  if (Number.isNaN(start)) return ativa?.tempoAtivoFormatado || ''
  const min = Math.max(0, Math.floor((now.value - start) / 60000))
  return formatMin(min)
}

const elapsedLabel = (u) => {
  if (!u.atividadeAtiva) return ''
  const tasks = activeTasks(u)
  if (tasks.length > 1) {
    return t('workspaceAtividade.userSummary.multipleActive', { count: tasks.length })
  }
  const desde = u.atividadeAtivaDesde || tasks[0]?.dataInicio
  if (!desde) return u.tempoAtivoFormatado || tasks[0]?.tempoAtivoFormatado || '—'
  const start = new Date(desde).getTime()
  if (Number.isNaN(start)) return u.tempoAtivoFormatado || '—'
  const min = Math.max(0, Math.floor((now.value - start) / 60000))
  return formatMin(min)
}
</script>
